import type { ContentType } from '@satyanand/shared'

export const adminQueryKeys = {
  admins: () => ['admin', 'admins'] as const,
  root: () => ['admin'] as const,
  dashboard: () => ['admin', 'dashboard'] as const,
  contentRoot: () => ['admin', 'content'] as const,
  contentList: (params: {
    q?: string
    type?: ContentType | 'all'
    categoryId?: string
    status?: 'all' | 'draft' | 'published' | 'archived'
  }) =>
    [
      'admin',
      'content',
      'list',
      params.q ?? '',
      params.type ?? 'all',
      params.categoryId ?? 'all',
      params.status ?? 'all',
    ] as const,
  users: () => ['admin', 'users'] as const,
  usersList: (params: {
    page: number
    pageSize: number
    search?: string
    sortBy?: string
    sortDir?: 'asc' | 'desc'
  }) =>
    [
      'admin',
      'users',
      'list',
      params.page,
      params.pageSize,
      params.search ?? '',
      params.sortBy ?? 'created_at',
      params.sortDir ?? 'desc',
    ] as const,
  userById: (id: string) => ['admin', 'users', id] as const,
  courses: () => ['admin', 'courses'] as const,
  coursesList: (params: { sortBy: string; sortDir: 'asc' | 'desc' }) =>
    ['admin', 'courses', 'list', params.sortBy, params.sortDir] as const,
  courseById: (courseId?: string | null) =>
    ['admin', 'courses', 'detail', courseId ?? ''] as const,
  lessons: (courseId?: string | null) =>
    ['admin', 'lessons', courseId ?? 'all'] as const,
  categories: () => ['admin', 'categories'] as const,
  categoriesList: () => ['admin', 'categories', 'list'] as const,
  campaigns: () => ['admin', 'campaigns'] as const,
  donations: () => ['admin', 'donations'] as const,
  donationsList: (params: {
    page: number
    pageSize: number
    campaignId?: string
    search?: string
  }) =>
    [
      'admin',
      'donations',
      'list',
      params.page,
      params.pageSize,
      params.campaignId ?? 'all',
      params.search ?? '',
    ] as const,
  purchases: () => ['admin', 'purchases'] as const,
  purchasesList: (params: {
    page: number
    pageSize: number
    status?: string
    search?: string
    purchaseType?: string
  }) =>
    [
      'admin',
      'purchases',
      'list',
      params.page,
      params.pageSize,
      params.status ?? 'all',
      params.search ?? '',
      params.purchaseType ?? 'all',
    ] as const,
  subscriptions: (page?: number) =>
    ['admin', 'subscriptions', page ?? 1] as const,
  subscriptionsRoot: () => ['admin', 'subscriptions'] as const,
  membershipsRoot: () => ['admin', 'memberships'] as const,
  memberships: (page?: number, tier?: string) =>
    ['admin', 'memberships', page ?? 1, tier ?? 'all'] as const,
  paymentTransactions: (params?: {
    page?: number
    sortBy?: string
    sortDir?: 'asc' | 'desc'
    q?: string
    status?: string
    type?: string
    source?: string
    hasMismatch?: boolean
  }) =>
    [
      'admin',
      'payment-transactions',
      params?.page ?? 1,
      params?.sortBy ?? 'created_at',
      params?.sortDir ?? 'desc',
      params?.q ?? '',
      params?.status ?? 'all',
      params?.type ?? 'all',
      params?.source ?? 'all',
      String(params?.hasMismatch ?? false),
    ] as const,
  invoices: (params?: {
    page?: number
    pageSize?: number
    q?: string
    status?: string
    type?: string
    subscriptionId?: string
  }) =>
    [
      'admin',
      'invoices',
      params?.page ?? 1,
      params?.pageSize ?? 20,
      params?.q ?? '',
      params?.status ?? 'all',
      params?.type ?? 'all',
      params?.subscriptionId ?? '',
    ] as const,
  subscriptionBilling: (params?: {
    page?: number
    pageSize?: number
    q?: string
    status?: string
  }) =>
    [
      'admin',
      'subscriptions',
      'billing',
      params?.page ?? 1,
      params?.pageSize ?? 20,
      params?.q ?? '',
      params?.status ?? 'all',
    ] as const,
  subscriptionInvoices: (subscriptionId?: string) =>
    ['admin', 'subscriptions', 'invoices', subscriptionId ?? ''] as const,
  messageLogsRoot: () => ['admin', 'message-logs'] as const,
  messageLogs: (filters?: string) =>
    ['admin', 'message-logs', filters ?? 'default'] as const,
  notificationLogs: () => ['admin', 'notification-logs'] as const,
  whatsappTemplates: () => ['admin', 'whatsapp-templates'] as const,
  remindersConfig: () => ['admin', 'reminders', 'config'] as const,
  remindersLogs: (filters?: string) =>
    ['admin', 'reminders', 'logs', filters ?? 'default'] as const,
  emailTemplates: () => ['admin', 'email-templates'] as const,
  liveSessions: () => ['admin', 'live-sessions'] as const,
  adminSessions: () => ['admin', 'sessions'] as const,
  dailyFreeSession: () => ['admin', 'daily-free-session'] as const,
  subscriptionSessions: () => ['admin', 'subscription-sessions'] as const,
  zoomSettings: () => ['admin', 'zoom-settings'] as const,
  homeCarousel: () => ['admin', 'home-carousel'] as const,
  receiptSettings: () => ['admin', 'receipt-settings'] as const,
  sadhana: () => ['admin', 'sadhana'] as const,
  certificates: (params?: {
    userId?: string
    courseId?: string
    fromDate?: string
  }) =>
    [
      'admin',
      'certificates',
      params?.userId ?? '',
      params?.courseId ?? '',
      params?.fromDate ?? '',
    ] as const,
} as const
